import {
  CaregiverDto,
  InfantDto,
  MotherDto,
  SiteAddressDto,
  UserInfoDto,
  VisitDto,
} from '@/../../../packages/core/lib';
import {
  CaregiverModelInput,
  EventRecord,
  EventRecordType,
  InfantModelInput,
  MotherChildVisitModelInput,
  SiteAddressInput,
  VisitDataStatus,
  VisitDataStatusFilterInput,
  VisitModelInput,
} from '@ecdlink/graphql';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { InfantService } from '@/services/InfantService';
import { RootState, ThunkApiType } from '../types';
import { EventRecordService } from '@/services/EventRecordService';
import { Referral } from '@/services/ReferralService';

export const InfantActions = {
  ADD_INFANTS: 'addInfant',
  UPDATE_INFANT: 'updateInfant',
  GET_INFANTS: 'getInfants',
  GET_INFANT_VISITS: 'getInfantVisits',
  GET_ALL_INFANT_EVENT_RECORD_TYPES: 'getAllInfantEventRecordTypes',
  UPDATE_INFANT_CAREGIVER: 'updateInfantCaregiver',
  GET_REFERRALS_FOR_INFANT: 'getReferralsForInfant',
  GET_COMPLETED_REFERRALS_FOR_INFANT: 'getCompletedReferralsForInfant',
  UPDATE_VISIT_DATA_STATUS: 'updateVisitDataStatus',
  ADD_ADDITIONAL_VISIT_FOR_INFANT: 'addAdditionalVisitForInfant',
  GET_EVENT_RECORD_BY_ID: 'getEventRecordById',
  GET_INFANT_STATUS_INFO_FOR_IDS: 'getInfantStatusInfoForIds',
  GET_ALL_VISITS_FOR_INFANT_IDS: 'getAllVisitsForInfantIds',
  SYNC_INFANT_VISITS: 'syncInfantVisits',
  SYNC_INFANT_EVENT_RECORDS: 'syncInfantEventRecords',
};

export interface UpdateInfantCaregiver {
  infantId: string;
  input: InfantModelInput;
}

export const syncInfantVisits = createAsyncThunk<
  any,
  void, // argument type (none)
  ThunkApiType<RootState>
>(
  InfantActions.SYNC_INFANT_VISITS,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      infants: { infants },
    } = getState();

    if (!userAuth?.auth_token || !infants?.length) {
      return { success: true }; // Nothing to sync
    }

    try {
      if (userAuth?.auth_token && infants) {
        for (const infant of infants) {
          const unsyncedVisits =
            infant.visits?.filter((v) => 'synced' in v && !v.synced) ?? [];
          if (unsyncedVisits.length > 0) {
            await new InfantService(userAuth?.auth_token).syncInfantVisits(
              unsyncedVisits
            );
          }
        }
      }
      return [true];
    } catch (err) {
      console.error('Failed to sync infant visits:', err);
      return rejectWithValue(err);
    }
  }
);

export const syncInfantEventRecords = createAsyncThunk<
  any,
  void, // argument type (none)
  ThunkApiType<RootState>
>(
  InfantActions.SYNC_INFANT_EVENT_RECORDS,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      infants: { infants },
    } = getState();

    if (!userAuth?.auth_token || !infants?.length) {
      return { success: true }; // Nothing to sync
    }

    try {
      if (userAuth?.auth_token && infants) {
        for (const infant of infants) {
          if (infant.eventRecords) {
            for (const element of infant.eventRecords) {
              if (!element.synced) {
                await new EventRecordService(
                  userAuth?.auth_token
                ).addEventRecord(element);
              }
            }
          }
        }
      }
      return [true];
    } catch (err) {
      console.error('Failed to sync mother event record:', err);
      return rejectWithValue(err);
    }
  }
);

export const getInfants = createAsyncThunk<
  InfantDto[],
  void, // argument type (none)
  ThunkApiType<RootState>
>(InfantActions.GET_INFANTS, async (_, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
    infants: { infants: cache },
    user: { user: userCache },
  } = getState();

  const cacheBelongsToAuthUser =
    !!userAuth?.id && (!userCache?.id || userCache.id === userAuth.id);

  // Return cached data immediately if present and for this user
  if (cacheBelongsToAuthUser && cache && cache.length > 0) {
    return cache;
  }

  // Ensure we have a token
  if (!userAuth?.auth_token || !userAuth?.id) {
    return rejectWithValue(
      'no access token or user id, authentication required'
    );
  }

  try {
    const infants = await new InfantService(
      userAuth?.auth_token
    ).GetAllInfantsForMother();
    return infants;
  } catch (err: any) {
    console.error('Failed to fetch infants:', err);
    return rejectWithValue(
      err?.response?.data?.message || err?.message || 'Failed to fetch infants'
    );
  }
});

export const getAllVisitsForInfantIds = createAsyncThunk<
  VisitDto[],
  string[],
  { state: RootState; rejectValue: string | unknown }
>(
  InfantActions.GET_ALL_VISITS_FOR_INFANT_IDS,
  async (infantIds, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    if (!userAuth?.auth_token) {
      return rejectWithValue('no access token, profile check required');
    }

    try {
      const visits = await new InfantService(
        userAuth.auth_token
      ).getAllVisitsForInfantIds(infantIds);

      return visits;
    } catch (err: any) {
      return rejectWithValue(err.message || err);
    }
  }
);

export const getInfantStatusInfoForIds = createAsyncThunk<
  UserInfoDto[],
  void,
  { state: RootState; rejectValue: string | [] }
>(
  InfantActions.GET_INFANT_STATUS_INFO_FOR_IDS,
  async (_, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      infants: { infants },
    } = getState();

    if (!infants) return [];

    const infantIds = infants
      .map((m) => m.isActive && m?.user?.id!)
      .filter(Boolean) as string[];

    if (!userAuth?.auth_token || !userAuth?.id) {
      return rejectWithValue(
        'no access token or user id, authentication required'
      );
    }

    try {
      return await new InfantService(
        userAuth.auth_token
      ).getInfantStatusInfoForIds(infantIds);
    } catch (err: any) {
      console.error('Failed to fetch infant info:', err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to fetch infant info'
      );
    }
  }
);

type CreateInfantRequest = {
  infant: InfantDto;
  motherId?: MotherDto['id'];
};

export const addInfant = createAsyncThunk<
  { motherId?: MotherDto['id'] },
  CreateInfantRequest,
  ThunkApiType<RootState>
>(
  InfantActions.ADD_INFANTS,
  async ({ infant, motherId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let mappedInfantInput = mapInfant(infant);
      const mappedVisits = infant.visits!.map((item) => mapVisit(item));
      if (userAuth?.auth_token) {
        await new InfantService(userAuth?.auth_token).addInfant(
          mappedInfantInput,
          mappedVisits
        );
        return { motherId };
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const upsertInfants = createAsyncThunk<
  boolean[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>('upsertInfants', async (_, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
    infants: { infants },
  } = getState();

  try {
    if (userAuth?.auth_token && infants) {
      for (const infant of infants) {
        let mappedInfantInput = mapSyncedInfant(infant);

        if ('isNew' in infant && infant.isNew === true) {
          const mappedVisits = infant.visits!.map((item) => mapVisit(item));
          await new InfantService(userAuth?.auth_token).addInfant(
            mappedInfantInput,
            mappedVisits
          );
        } else {
          if ('synced' in infant && infant.synced === false) {
            await new InfantService(userAuth?.auth_token).updateInfant(
              infant.id ?? '',
              mappedInfantInput
            );
          }
        }
      }
    }
    return [true];
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const updateInfant = createAsyncThunk<
  InfantDto,
  { input: InfantModelInput; id: string },
  ThunkApiType<RootState>
>(
  InfantActions.UPDATE_INFANT,
  async ({ id, input }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      if (userAuth?.auth_token) {
        const response = await new InfantService(
          userAuth?.auth_token
        ).updateInfant(id, input);

        return response;
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getInfantVisits = createAsyncThunk<
  VisitDto[],
  { infantId: string },
  ThunkApiType<RootState>
>(
  InfantActions.GET_INFANT_VISITS,
  async ({ infantId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      infants: { infants },
    } = getState();

    // Find the infant in state
    const infant = infants?.find(
      (m) => m.id === infantId || m.userId === infantId
    );
    const cachedVisitsForInfant = infant?.visits || [];

    // Return cached data if exists and has at least one visit
    if (cachedVisitsForInfant.length > 0) {
      return cachedVisitsForInfant;
    }

    // No cache — fetch from server
    if (!userAuth?.auth_token) {
      return rejectWithValue('no access token, profile check required');
    }

    try {
      return await new InfantService(userAuth.auth_token).GetInfantVisits(
        infantId
      );
    } catch (err: any) {
      return rejectWithValue(err.message || err);
    }
  }
);

export const updateInfantCaregiverAddress = createAsyncThunk<
  InfantDto,
  UpdateMotherRequest,
  ThunkApiType<RootState>
>(
  'updateInfantCaregiverAddress',
  async ({ infant, id }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let mappedInfantInput = mapInfant(infant);
      if (userAuth?.auth_token) {
        return await new InfantService(
          userAuth?.auth_token
        ).updateInfantCaregiverAddress(id, mappedInfantInput);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateInfantCaregiverContactDetails = createAsyncThunk<
  InfantDto,
  UpdateMotherRequest,
  ThunkApiType<RootState>
>(
  'updateInfantCaregiverContactDetails',
  async ({ infant, id }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let mappedInfantInput = mapInfant(infant);
      if (userAuth?.auth_token) {
        return await new InfantService(
          userAuth?.auth_token
        ).updateInfantCaregiverContactDetails(id, mappedInfantInput);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getAllInfantEventRecordTypes = createAsyncThunk<
  EventRecordType[],
  undefined,
  ThunkApiType<RootState>
>(
  InfantActions.GET_ALL_INFANT_EVENT_RECORD_TYPES,
  async (_, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      infants: { eventRecordTypes: eventRecordTypesCache },
    } = getState();

    try {
      if (eventRecordTypesCache && eventRecordTypesCache.length > 0) {
        return eventRecordTypesCache;
      }

      if (!userAuth?.auth_token) {
        return rejectWithValue('no access token, profile check required');
      }

      const eventRecordTypes = await new EventRecordService(
        userAuth.auth_token
      ).getAllEventRecordTypes('child');

      return eventRecordTypes;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateInfantCaregiver = createAsyncThunk<
  InfantDto,
  UpdateInfantCaregiver,
  ThunkApiType<RootState>
>(
  InfantActions.UPDATE_INFANT_CAREGIVER,
  async ({ infantId, input }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        return await new InfantService(
          userAuth?.auth_token
        ).updateInfantCaregiver(infantId, input);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addAdditionalVisitForInfant = createAsyncThunk<
  VisitDto,
  VisitModelInput,
  ThunkApiType<RootState>
>(
  InfantActions.ADD_ADDITIONAL_VISIT_FOR_INFANT,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const response = await new InfantService(
          userAuth?.auth_token
        ).addAdditionalVisitForChild(input);

        return response;
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getReferralsForInfant = createAsyncThunk<
  VisitDataStatus[],
  { infantId: string; visitId: string },
  ThunkApiType<RootState>
>(
  InfantActions.GET_REFERRALS_FOR_INFANT,
  async ({ infantId, visitId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      let referrals: VisitDataStatus[];

      if (userAuth?.auth_token) {
        referrals = await new Referral(
          userAuth?.auth_token
        ).getReferralsForInfant(infantId, visitId);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!referrals) {
        return rejectWithValue('Error getting more information');
      }
      return referrals;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getCompletedReferralsForInfant = createAsyncThunk<
  VisitDataStatus[],
  { infantId: string; visitId: string },
  ThunkApiType<RootState>
>(
  InfantActions.GET_COMPLETED_REFERRALS_FOR_INFANT,
  async ({ infantId, visitId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      let referrals: VisitDataStatus[];

      if (userAuth?.auth_token) {
        referrals = await new Referral(
          userAuth?.auth_token
        ).getCompletedReferralsForInfant(infantId, visitId);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!referrals) {
        return rejectWithValue('Error getting more information');
      }
      return referrals;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateVisitDataStatus = createAsyncThunk<
  {},
  { input: VisitDataStatusFilterInput[] },
  ThunkApiType<RootState>
>(
  InfantActions.UPDATE_VISIT_DATA_STATUS,
  async ({ input }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        new Referral(userAuth?.auth_token ?? '').updateVisitDataStatus(input);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getEventRecordsByClientId = createAsyncThunk<
  EventRecord[],
  { clientId: string },
  ThunkApiType<RootState>
>(
  InfantActions.GET_EVENT_RECORD_BY_ID,
  async ({ clientId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      infants: { infants },
    } = getState();

    const infant = infants?.find(
      (m) => m.id === clientId || m.userId === clientId
    );
    const cachedEventRecordsForInfant = infant?.eventRecords || [];

    if (cachedEventRecordsForInfant.length > 0) {
      return cachedEventRecordsForInfant as EventRecord[];
    }

    if (!userAuth?.auth_token || !userAuth?.id) {
      return rejectWithValue(
        'no access token or user id, authentication required'
      );
    }

    try {
      return await new EventRecordService(
        userAuth?.auth_token
      ).getEventRecordsByClientId(clientId);
    } catch (err: any) {
      // Preserve the actual error for debugging/logging
      console.error('Failed to event records:', err);
      // You can customize the rejected payload here
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to fetch event records'
      );
    }
  }
);

export type UpdateMotherRequest = {
  id: string;
  infant: InfantDto;
};

const mapInfant = (x: Partial<InfantDto>): InfantModelInput => ({
  dateOfBirth: x.dateOfBirth,
  firstName: x.firstName,
  caregiverId: x.caregiverId,
  caregiver: mapCaregiver(x.caregiver!),
  userId: x.userId,
  genderId: x.genderId,
  weightAtBirth: x.weightAtBirth?.toString(),
  lengthAtBirth: x.lengthAtBirth?.toString(),
  completed24MonthVisits: x.visitInfo?.completed24MonthVisits,
});

const mapSyncedInfant = (x: Partial<InfantDto>): InfantModelInput => ({
  dateOfBirth: x.dateOfBirth ?? x?.user?.dateOfBirth,
  firstName: x?.firstName,
  caregiverId: x?.caregiver?.id,
  caregiver: mapCaregiver(x?.caregiver! ?? x?.mother!),
  userId: x.id ?? x?.userId,
  genderId: x?.genderId,
  weightAtBirth: x.weightAtBirth?.toString(),
  lengthAtBirth: x.lengthAtBirth?.toString(),
  completed24MonthVisits: x.visitInfo?.completed24MonthVisits,
});

const mapCaregiver = (x: Partial<CaregiverDto>): CaregiverModelInput => ({
  firstName: x.firstName,
  surname: x.surname,
  phoneNumber: x.phoneNumber,
  whatsAppNumber: x.whatsAppNumber,
  healthCareWorkerId: x.healthCareWorkerId,
  age: x.age,
  relationId: x.relationId,
  siteAddress: x.siteAddress ? mapSiteAddress(x.siteAddress!) : null,
});

const mapSiteAddress = (x: Partial<SiteAddressDto>): SiteAddressInput => ({
  Id: x.id,
  AddressLine1: x.addressLine1,
  AddressLine2: x.addressLine2,
  AddressLine3: x.addressLine3,
  Name: x.name,
  PostalCode: x.postalCode,
  ProvinceId: x.provinceId,
  Ward: x.ward,
  IsActive: x.isActive === false ? false : true,
});

const mapVisit = (x: Partial<VisitDto>): MotherChildVisitModelInput => ({
  id: x.id,
  dueDate: x.dueDate,
  plannedVisitDate: x.plannedVisitDate,
  visitTypeId: x.visitType?.id,
  clientId: x.clientId,
  insertedDate: x.insertedDate,
});
